import { osTypeValues, type OsType } from './api';
import type { AgentConfig } from './api';

/**
 * 러너 요청(모달·홈 피드 드롭)에서 에이전트 후보를 좁히는 공용 술어 SSOT.
 *
 * 모달 훅(`useDaemonTriggerModal`)에서 떼어내 별도 모듈로 둔다. 모달 모듈은 러너 타입·모델·
 * 워크트리까지 끌고 오는 대형 모듈이라, 후보 판정만 필요한 쪽이 그것을 통째로 import하면
 * 번들과 테스트 셋업이 함께 무거워진다. 두 입력 수단이 같은 후보 집합을 보게 하려면
 * 판정은 반드시 이 한 곳에만 있어야 한다.
 */
export const isDaemonTriggerSelectableAgentConfig = (config: Pick<AgentConfig, 'authPathFolderName' | 'osType'>) =>
  Boolean(config.authPathFolderName?.trim()) && Boolean(config.osType);

export const scopeAgentConfigsToSelectedDaemon = <T extends Pick<AgentConfig, 'targetDaemonId'>>(
  agentConfigs: T[],
  selectedDaemon: { id: string; ownerMemberId?: string | null } | null,
): T[] => {
  if (!selectedDaemon?.id) {
    // 데몬 미선택: 특정 데몬에 바인딩된 Agent는 숨깁니다.
    return agentConfigs.filter((config) => !config.targetDaemonId);
  }

  // 선택된 러너에 명시적으로 연결된 Agent만 요청 후보로 노출합니다.
  return agentConfigs.filter((config) => config.targetDaemonId === selectedDaemon.id);
};

export const getCompatibleAgentConfigsForDaemon = <T extends Pick<AgentConfig, 'osType' | 'targetDaemonId'>>(
  agentConfigs: T[],
  selectedDaemon: { id: string; osType: OsType | null | undefined; ownerMemberId?: string | null } | null,
) => {
  if (!selectedDaemon) {
    return agentConfigs;
  }

  const scoped = scopeAgentConfigsToSelectedDaemon(agentConfigs, selectedDaemon);

  const daemonOsType = selectedDaemon.osType;
  if (!daemonOsType || !osTypeValues.includes(daemonOsType)) {
    return scoped;
  }

  return scoped.filter((config) => config.osType === daemonOsType);
};
